import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { settingService } from "../services/settingService";

export const SettingsContext = createContext(null);

const CACHE_KEY = "dalatTour.settings.v1";
const CACHE_TTL_MS = 5 * 60 * 1000;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || !parsed?.data) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => readCache());
  const [loading, setLoading] = useState(!settings);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await settingService.getSettings();
      setSettings(s || null);
      if (s) writeCache(s);
      return s;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (settings) return;
    refresh();
  }, [settings, refresh]);

  const value = useMemo(
    () => ({
      settings,
      loading,
      error,
      refresh,
      setSettings, // for advanced usage (admin) if needed
    }),
    [settings, loading, error, refresh]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettings must be used within SettingsProvider");
  }
  return ctx;
}

